/**
 * Mock scheduled report data for MSP reporting prototype
 * Represents recurring report schedules
 */

export interface ScheduledReport {
  id: string;
  report_type: string;
  report_type_name: string;
  customer_ids: number[];
  customer_names: string[];
  frequency: 'weekly' | 'monthly';
  day_of_week?: number; // 0-6 for weekly (0 = Sunday)
  day_of_month?: number; // 1-31 for monthly
  time_of_day: string; // HH:MM format
  enabled: boolean;
  next_run: string;
  last_run: string | null;
  created_at: string;
  created_by: string;
  configuration: {
    include_executive_summary: boolean;
    include_detailed_findings: boolean;
    include_remediation: boolean;
    delivery_method: 'download' | 'email' | 'storage';
    email_recipients?: string[];
    storage_destination?: string;
  };
  execution_history?: {
    date: string;
    status: 'success' | 'failed';
    reports_generated: number;
  }[];
}

export const scheduledReportsMockData: ScheduledReport[] = [
  {
    id: 'sr-001',
    report_type: 'MONTHLY_SERVICE_REVIEW',
    report_type_name: 'Monthly Service Review',
    customer_ids: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 19, 20],
    customer_names: [
      'Acme Corporation',
      'TechStart Inc',
      'Global Services LLC',
      'Bright Future Academy',
      'MediCare Partners',
      'Downtown Dental Group',
      'First National Bank',
      'Precision Manufacturing Co',
      'Green Energy Solutions',
      'Quantum Tech Labs',
      'Riverside Hospital',
      'Summit Consulting Group',
      'Pacific Insurance Co',
      'Midwest Credit Union',
      'Harbor Logistics',
      'Sunrise Retail Chain',
      'Atlas Construction',
      'Evergreen Properties',
    ],
    frequency: 'monthly',
    day_of_month: 1,
    time_of_day: '09:00',
    enabled: true,
    next_run: '2025-11-01T09:00:00Z',
    last_run: '2025-10-01T09:00:00Z',
    created_at: '2025-01-15T10:00:00Z',
    created_by: 'admin@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['customers@msp.com', 'management@msp.com'],
    },
    execution_history: [
      {
        date: '2025-10-01T09:00:00Z',
        status: 'success',
        reports_generated: 18,
      },
      {
        date: '2025-09-01T09:00:00Z',
        status: 'success',
        reports_generated: 17,
      },
      {
        date: '2025-08-01T09:00:00Z',
        status: 'success',
        reports_generated: 16,
      },
    ],
  },
  {
    id: 'sr-002',
    report_type: 'HIPAA_COMPLIANCE',
    report_type_name: 'HIPAA Compliance Report',
    customer_ids: [1, 5, 6, 11, 14, 20],
    customer_names: [
      'Acme Corporation',
      'MediCare Partners',
      'Downtown Dental Group',
      'Riverside Hospital',
      'Midwest Credit Union',
      'Evergreen Properties',
    ],
    frequency: 'weekly',
    day_of_week: 1, // Monday
    time_of_day: '08:00',
    enabled: true,
    next_run: '2025-10-07T08:00:00Z',
    last_run: '2025-09-30T08:00:00Z',
    created_at: '2025-02-01T14:30:00Z',
    created_by: 'compliance@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'storage',
      storage_destination: 'SharePoint',
    },
    execution_history: [
      {
        date: '2025-09-30T08:00:00Z',
        status: 'success',
        reports_generated: 6,
      },
      {
        date: '2025-09-23T08:00:00Z',
        status: 'success',
        reports_generated: 6,
      },
      {
        date: '2025-09-16T08:00:00Z',
        status: 'failed',
        reports_generated: 0,
      },
    ],
  },
  {
    id: 'sr-003',
    report_type: 'EXECUTIVE_REPORT',
    report_type_name: 'Executive Summary',
    customer_ids: [1, 5, 7, 11, 13],
    customer_names: [
      'Acme Corporation',
      'MediCare Partners',
      'First National Bank',
      'Riverside Hospital',
      'Pacific Insurance Co',
    ],
    frequency: 'weekly',
    day_of_week: 5, // Friday
    time_of_day: '16:00',
    enabled: true,
    next_run: '2025-10-04T16:00:00Z',
    last_run: '2025-09-27T16:00:00Z',
    created_at: '2025-03-10T11:00:00Z',
    created_by: 'admin@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: false,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['executives@msp.com'],
    },
    execution_history: [
      {
        date: '2025-09-27T16:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
      {
        date: '2025-09-20T16:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
    ],
  },
  {
    id: 'sr-004',
    report_type: 'NIST_800_171',
    report_type_name: 'NIST 800-171 Compliance Report',
    customer_ids: [3, 8, 13],
    customer_names: [
      'Global Services LLC',
      'Precision Manufacturing Co',
      'Pacific Insurance Co',
    ],
    frequency: 'monthly',
    day_of_month: 15,
    time_of_day: '10:00',
    enabled: true,
    next_run: '2025-10-15T10:00:00Z',
    last_run: '2025-09-15T10:00:00Z',
    created_at: '2025-04-01T09:00:00Z',
    created_by: 'compliance@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'storage',
      storage_destination: 'Egnyte',
    },
    execution_history: [
      {
        date: '2025-09-15T10:00:00Z',
        status: 'success',
        reports_generated: 3,
      },
      {
        date: '2025-08-15T10:00:00Z',
        status: 'success',
        reports_generated: 3,
      },
    ],
  },
  {
    id: 'sr-005',
    report_type: 'SOC_EVENTS_REPORT',
    report_type_name: 'SOC Events Report',
    customer_ids: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 19, 20],
    customer_names: [
      'Acme Corporation',
      'TechStart Inc',
      'Global Services LLC',
      'Bright Future Academy',
      'MediCare Partners',
      'Downtown Dental Group',
      'First National Bank',
      'Precision Manufacturing Co',
      'Green Energy Solutions',
      'Quantum Tech Labs',
      'Riverside Hospital',
      'Summit Consulting Group',
      'Pacific Insurance Co',
      'Midwest Credit Union',
      'Harbor Logistics',
      'Sunrise Retail Chain',
      'Atlas Construction',
      'Evergreen Properties',
    ],
    frequency: 'weekly',
    day_of_week: 1, // Monday
    time_of_day: '07:00',
    enabled: true,
    next_run: '2025-10-07T07:00:00Z',
    last_run: '2025-09-30T07:00:00Z',
    created_at: '2025-01-20T08:00:00Z',
    created_by: 'analyst@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
    },
    execution_history: [
      {
        date: '2025-09-30T07:00:00Z',
        status: 'success',
        reports_generated: 18,
      },
      {
        date: '2025-09-23T07:00:00Z',
        status: 'success',
        reports_generated: 17,
      },
    ],
  },
  {
    id: 'sr-006',
    report_type: 'VULNERABILITY_REPORT',
    report_type_name: 'Vulnerability Report',
    customer_ids: [1, 5, 7, 11, 13, 17],
    customer_names: [
      'Acme Corporation',
      'MediCare Partners',
      'First National Bank',
      'Riverside Hospital',
      'Pacific Insurance Co',
      'Atlas Construction',
    ],
    frequency: 'weekly',
    day_of_week: 3, // Wednesday
    time_of_day: '12:00',
    enabled: true,
    next_run: '2025-10-09T12:00:00Z',
    last_run: '2025-10-02T12:00:00Z',
    created_at: '2025-05-15T10:00:00Z',
    created_by: 'security@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'email',
      email_recipients: ['security-team@msp.com', 'management@msp.com'],
    },
    execution_history: [
      {
        date: '2025-10-02T12:00:00Z',
        status: 'success',
        reports_generated: 6,
      },
      {
        date: '2025-09-25T12:00:00Z',
        status: 'success',
        reports_generated: 6,
      },
    ],
  },
  {
    id: 'sr-007',
    report_type: 'SLA_ADHERENCE_DATA_REPORT',
    report_type_name: 'SLA Adherence Report',
    customer_ids: [1, 5, 7, 11, 13],
    customer_names: [
      'Acme Corporation',
      'MediCare Partners',
      'First National Bank',
      'Riverside Hospital',
      'Pacific Insurance Co',
    ],
    frequency: 'monthly',
    day_of_month: 5,
    time_of_day: '14:00',
    enabled: true,
    next_run: '2025-11-05T14:00:00Z',
    last_run: '2025-10-05T14:00:00Z',
    created_at: '2025-02-10T13:00:00Z',
    created_by: 'admin@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['management@msp.com'],
    },
    execution_history: [
      {
        date: '2025-10-05T14:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
      {
        date: '2025-09-05T14:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
    ],
  },
  {
    id: 'sr-008',
    report_type: 'O365_ACCOUNT_ACTIVITY_REPORT',
    report_type_name: 'O365 Account Activity Report',
    customer_ids: [2, 4, 6, 9, 10, 12, 14, 17, 20],
    customer_names: [
      'TechStart Inc',
      'Bright Future Academy',
      'Downtown Dental Group',
      'Green Energy Solutions',
      'Quantum Tech Labs',
      'Summit Consulting Group',
      'Midwest Credit Union',
      'Atlas Construction',
      'Evergreen Properties',
    ],
    frequency: 'weekly',
    day_of_week: 2, // Tuesday
    time_of_day: '10:00',
    enabled: false, // Disabled for testing
    next_run: '2025-10-08T10:00:00Z',
    last_run: '2025-09-24T10:00:00Z',
    created_at: '2025-03-20T11:30:00Z',
    created_by: 'analyst@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
    },
    execution_history: [
      {
        date: '2025-09-24T10:00:00Z',
        status: 'success',
        reports_generated: 9,
      },
    ],
  },
  {
    id: 'sr-009',
    report_type: 'GENERAL_SECURITY',
    report_type_name: 'General Security Report',
    customer_ids: [9, 12, 15, 19],
    customer_names: [
      'Green Energy Solutions',
      'Summit Consulting Group',
      'Harbor Logistics',
      'Sunrise Retail Chain',
    ],
    frequency: 'monthly',
    day_of_month: 20,
    time_of_day: '11:00',
    enabled: true,
    next_run: '2025-10-20T11:00:00Z',
    last_run: '2025-09-20T11:00:00Z',
    created_at: '2025-06-01T09:00:00Z',
    created_by: 'admin@msp.com',
    configuration: {
      include_executive_summary: true,
      include_detailed_findings: true,
      include_remediation: true,
      delivery_method: 'email',
      email_recipients: ['customers@msp.com'],
    },
    execution_history: [
      {
        date: '2025-09-20T11:00:00Z',
        status: 'success',
        reports_generated: 4,
      },
      {
        date: '2025-08-20T11:00:00Z',
        status: 'success',
        reports_generated: 4,
      },
    ],
  },
  {
    id: 'sr-010',
    report_type: 'ESCALATED_CASES_REPORT',
    report_type_name: 'Escalated Cases Report',
    customer_ids: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 19, 20],
    customer_names: [
      'Acme Corporation',
      'TechStart Inc',
      'Global Services LLC',
      'Bright Future Academy',
      'MediCare Partners',
      'Downtown Dental Group',
      'First National Bank',
      'Precision Manufacturing Co',
      'Green Energy Solutions',
      'Quantum Tech Labs',
      'Riverside Hospital',
      'Summit Consulting Group',
      'Pacific Insurance Co',
      'Midwest Credit Union',
      'Harbor Logistics',
      'Sunrise Retail Chain',
      'Atlas Construction',
      'Evergreen Properties',
    ],
    frequency: 'monthly',
    day_of_month: 10,
    time_of_day: '15:00',
    enabled: true,
    next_run: '2025-10-10T15:00:00Z',
    last_run: '2025-09-10T15:00:00Z',
    created_at: '2025-04-15T10:00:00Z',
    created_by: 'support@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
    },
    execution_history: [
      {
        date: '2025-09-10T15:00:00Z',
        status: 'success',
        reports_generated: 18,
      },
      {
        date: '2025-08-10T15:00:00Z',
        status: 'success',
        reports_generated: 17,
      },
    ],
  },
  {
    id: 'sr-011',
    report_type: 'XDR_INGESTION_DATA_REPORT',
    report_type_name: 'XDR Ingestion Report',
    customer_ids: [1, 2, 4, 5, 7, 10, 11, 13, 15, 17, 19, 20],
    customer_names: [
      'Acme Corporation',
      'TechStart Inc',
      'Bright Future Academy',
      'MediCare Partners',
      'First National Bank',
      'Quantum Tech Labs',
      'Riverside Hospital',
      'Pacific Insurance Co',
      'Harbor Logistics',
      'Atlas Construction',
      'Sunrise Retail Chain',
      'Evergreen Properties',
    ],
    frequency: 'monthly',
    day_of_month: 25,
    time_of_day: '09:30',
    enabled: true,
    next_run: '2025-10-25T09:30:00Z',
    last_run: '2025-09-25T09:30:00Z',
    created_at: '2025-05-01T08:00:00Z',
    created_by: 'operations@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'email',
      email_recipients: ['operations@msp.com'],
    },
    execution_history: [
      {
        date: '2025-09-25T09:30:00Z',
        status: 'success',
        reports_generated: 12,
      },
      {
        date: '2025-08-25T09:30:00Z',
        status: 'success',
        reports_generated: 11,
      },
    ],
  },
  {
    id: 'sr-012',
    report_type: 'WINDOW_ACCOUNT_ACTIVITY_REPORT',
    report_type_name: 'Windows Account Activity Report',
    customer_ids: [3, 7, 8, 13, 15],
    customer_names: [
      'Global Services LLC',
      'First National Bank',
      'Precision Manufacturing Co',
      'Pacific Insurance Co',
      'Harbor Logistics',
    ],
    frequency: 'weekly',
    day_of_week: 4, // Thursday
    time_of_day: '13:00',
    enabled: true,
    next_run: '2025-10-10T13:00:00Z',
    last_run: '2025-10-03T13:00:00Z',
    created_at: '2025-06-15T10:00:00Z',
    created_by: 'analyst@msp.com',
    configuration: {
      include_executive_summary: false,
      include_detailed_findings: true,
      include_remediation: false,
      delivery_method: 'download',
    },
    execution_history: [
      {
        date: '2025-10-03T13:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
      {
        date: '2025-09-26T13:00:00Z',
        status: 'success',
        reports_generated: 5,
      },
    ],
  },
];

/**
 * Sample reports list For Dialog
 */
export const SampleReportsList: any[] = [
  {
    name: 'HIPAA Compliance Report',
    description: 'Healthcare compliance assessment with detailed HIPAA Security Rule analysis',
    icon: '🏥',
    features: ['Technical safeguards', 'Compliance matrix', 'Action items', 'Evidence packages'],
    sampleFile: 'assets/sample-reports/hipaa-sample-report.html',
    hasSample: true,
  },
  {
    name: 'NIST CSF Compliance Report',
    description: 'Comprehensive alignment with NIST Cybersecurity Framework 2.0',
    icon: '🛡️',
    features: ['Six core functions', 'Implementation tiers', 'Supply chain risk', 'Maturity roadmap'],
    sampleFile: 'assets/sample-reports/nist-csf-sample-report.html',
    hasSample: true,
  },
  {
    name: 'GDPR Compliance Report',
    description: 'EU data protection compliance assessment for credit unions',
    icon: '🇪🇺',
    features: ['Data subject rights', 'International transfers', 'Processor agreements', 'Breach notification'],
    sampleFile: 'assets/sample-reports/gdpr-sample-report.html',
    hasSample: true,
  },
  {
    name: 'Malicious Code Protection Report',
    description: 'EDR-based malicious code detection and protection evidence with automated compliance tracking',
    icon: '🦠',
    features: ['EDR detections', 'Update compliance', 'Scan configuration', 'Automated reporting'],
    sampleFile: 'assets/sample-reports/malicious-code-protection-sample-report.html',
    hasSample: true,
  },
  {
    name: 'Executive Summary',
    description: 'High-level overview of security posture across all managed customers',
    icon: '📊',
    features: ['Key metrics', 'Trend analysis', 'Risk overview', 'Customer comparison'],
    hasSample: false,
  },
  {
    name: 'Security Incident Report',
    description: 'Detailed analysis of security incidents and response activities',
    icon: '🚨',
    features: ['Incident timeline', 'Threat details', 'Response actions', 'Remediation status'],
    hasSample: false,
  },
  {
    name: 'Vulnerability Assessment',
    description: 'Comprehensive vulnerability analysis and prioritization',
    icon: '🔍',
    features: ['Vulnerability details', 'Risk scoring', 'Remediation guidance', 'Trend analysis'],
    hasSample: false,
  },
  {
    name: 'Threat Intelligence Report',
    description: 'Current threat landscape and intelligence insights',
    icon: '🎯',
    features: ['Threat actors', 'Attack vectors', 'Industry trends', 'Recommendations'],
    hasSample: false,
  },
];

/**
 * Get enabled scheduled reports only
 */
export const getEnabledScheduledReports = (): ScheduledReport[] => {
  return scheduledReportsMockData.filter((sr) => sr.enabled);
};

/**
 * Get upcoming scheduled reports (next 7 days)
 */
export const getUpcomingScheduledReports = (days: number = 7): ScheduledReport[] => {
  const now = new Date();
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + days);

  return scheduledReportsMockData.filter((sr) => {
    if (!sr.enabled) return false;
    const nextRun = new Date(sr.next_run);
    return nextRun >= now && nextRun <= futureDate;
  });
};

/**
 * Get scheduled report by ID
 */
export const getScheduledReportById = (id: string): ScheduledReport | undefined => {
  return scheduledReportsMockData.find((sr) => sr.id === id);
};

/**
 * Get scheduled reports by customer ID
 */
export const getScheduledReportsByCustomerId = (customerId: number): ScheduledReport[] => {
  return scheduledReportsMockData.filter((sr) => sr.customer_ids.includes(customerId));
};

/**
 * Get scheduled reports by frequency
 */
export const getScheduledReportsByFrequency = (frequency: 'weekly' | 'monthly'): ScheduledReport[] => {
  return scheduledReportsMockData.filter((sr) => sr.frequency === frequency);
};
